import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Map,
  Upload,
  FileSpreadsheet,
  Check,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Pencil,
  Plus,
} from 'lucide-react';
import type {
  EntitateSilvicaDto,
  PrimarieDto,
  OcolSilvicDto,
  ProjectTypeDto,
  TreeSpeciesDto,
  WorkTypeDto,
  RegionsImportPreview,
  RegionsImportCommitResponse,
  RegionsDuplicate,
} from '@forest/contracts';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Table, Th, Td } from '@/components/ui/Table';
import { api, apiError } from '@/lib/api-client';
import { useUiStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/sessionStore';
import { cn } from '@/lib/cn';
import {
  useAllPrimarii,
  useEntitatiSilvice,
  useOcoaleSilvice,
  useTreeSpecies,
  useWorkTypes,
  useProjectTypes,
  useUpdatePrimarie,
  useUpdateEntitateSilvica,
  useUpdateOcolSilvic,
  useUpdateTreeSpecies,
  useUpdateWorkType,
  useUpdateProjectType,
  useCreatePrimarie,
  useCreateOcolSilvic,
  useCreateTreeSpecies,
  useCreateWorkType,
  useCreateProjectType,
  useCreateEntitateSilvica,
} from '@/features/regions/useRegions';

type Step = 'upload' | 'preview' | 'confirm' | 'done';
type CommitMode = 'skip_invalid' | 'all_or_nothing';

export function RegionsImportPage() {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<RegionsImportPreview | null>(null);
  const [committed, setCommitted] = useState<RegionsImportCommitResponse | null>(null);
  const [mode, setMode] = useState<CommitMode>('skip_invalid');
  const [applyNameChanges, setApplyNameChanges] = useState(false);

  // Read-only state panel. Reuses the shared `useRegions` hooks so the
  // state panel and the editable section below share one query cache
  // (data sizes are tiny: ~900 primarii, 26 entitati, ~90 ocoale).
  const primariiQ = useAllPrimarii();
  const entitatiQ = useEntitatiSilvice();
  const ocoaleQ = useOcoaleSilvice();

  /** Which state block (if any) is expanded inline. Only one open at a
   *  time so the page doesn't grow unboundedly. */
  const [expanded, setExpanded] = useState<
    'primarii' | 'entitati' | 'ocoale' | null
  >(null);
  /** Per-block search input. Cleared when the block collapses. */
  const [blockSearch, setBlockSearch] = useState('');

  const parseMut = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('file', f);
      return api.post('regions/import/parse', { body: fd }).json<RegionsImportPreview>();
    },
    onSuccess: (p) => {
      setPreview(p);
      setStep('preview');
    },
    onError: (err: Error) => {
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const commitMut = useMutation({
    mutationFn: async (input: {
      batchId: string;
      mode: CommitMode;
      applyNameChanges: boolean;
    }) =>
      api
        .post('regions/import/commit', { json: input })
        .json<RegionsImportCommitResponse>(),
    onSuccess: (r) => {
      setCommitted(r);
      setStep('done');
      qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError: (err: Error) => {
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const reset = () => {
    setFile(null);
    setPreview(null);
    setCommitted(null);
    setStep('upload');
  };

  // Tree species + work types are now parsed and imported, so they are no
  // longer surfaced as "detected but ignored". Only the still-unparsed
  // planting-scheme block remains an out-of-scope note.
  const oosBlocksLabel = useMemo(() => {
    if (!preview) return '';
    const labels: string[] = [];
    if (preview.oosBlocks.plantingScheme) labels.push(t('admin:regions.preview.plantingScheme'));
    return labels.join(', ');
  }, [preview, t]);

  // Per Phase 1 FR-006: applyNameChanges should only be enableable when the
  // preview actually contains rows whose name/raion/membership changed.
  const hasMutableChanges = useMemo(() => {
    if (!preview) return false;
    const isChange = (r: { reason: string }) =>
      r.reason === 'name-changed' || r.reason === 'membership-changed';
    return (
      preview.primarii.duplicates.some(isChange) ||
      preview.entitatiSilvice.duplicates.some(isChange) ||
      preview.ocoaleSilvice.duplicates.some(isChange)
    );
  }, [preview]);

  return (
    <div className="animate-fade-in mx-auto max-w-5xl space-y-6 py-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-forest-100 text-forest-700">
          <Map className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t('admin:regions.title')}</h1>
          <p className="text-sm text-slate-500">{t('admin:regions.subtitle')}</p>
        </div>
      </div>

      {/* Current state panel */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-slate-700">{t('admin:regions.state.title')}</h2>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StateBlock
            label={t('admin:regions.state.primarii', {
              count: primariiQ.data?.pagination.total ?? 0,
            })}
            rows={(primariiQ.data?.items ?? []).map((p: PrimarieDto) => ({
              key: p.cod,
              text: `${p.cod} · ${p.name}`,
              extra: p.raion ?? undefined,
            }))}
            expanded={expanded === 'primarii'}
            onToggle={() => {
              setBlockSearch('');
              setExpanded(expanded === 'primarii' ? null : 'primarii');
            }}
            search={expanded === 'primarii' ? blockSearch : ''}
            onSearch={setBlockSearch}
            t={t}
          />
          <StateBlock
            label={t('admin:regions.state.entitatiSilvice', {
              count: entitatiQ.data?.total ?? 0,
            })}
            rows={(entitatiQ.data?.items ?? []).map(
              (e: EntitateSilvicaDto) => ({
                key: e.cod,
                text: `${e.cod} · ${e.name}`,
                extra: t('admin:regions.state.ocoaleCount', {
                  count: e.ocoale.length,
                  defaultValue: '{{count}} ocoale',
                }),
              }),
            )}
            expanded={expanded === 'entitati'}
            onToggle={() => {
              setBlockSearch('');
              setExpanded(expanded === 'entitati' ? null : 'entitati');
            }}
            search={expanded === 'entitati' ? blockSearch : ''}
            onSearch={setBlockSearch}
            t={t}
          />
          <StateBlock
            label={t('admin:regions.state.ocoaleSilvice', {
              count: ocoaleQ.data?.pagination.total ?? 0,
            })}
            rows={(ocoaleQ.data?.items ?? []).map((o: OcolSilvicDto) => ({
              key: o.cod,
              text: `${o.cod}`,
              extra: o.entitateSilvicaCod,
            }))}
            expanded={expanded === 'ocoale'}
            onToggle={() => {
              setBlockSearch('');
              setExpanded(expanded === 'ocoale' ? null : 'ocoale');
            }}
            search={expanded === 'ocoale' ? blockSearch : ''}
            onSearch={setBlockSearch}
            t={t}
          />
        </CardBody>
      </Card>

      {/* Inline edit + add of reference data (names / codes). The
          forestry-entity add form now lives inside the Entități silvice
          block here, alongside the other reference tables. */}
      <EditableReferenceSection />

      {/* Wizard */}
      <Card>
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-slate-500" />
              <h2 className="text-sm font-semibold text-slate-700">
                {step === 'upload' && t('admin:regions.upload.label')}
                {step === 'preview' && t('admin:regions.preview.title')}
                {step === 'confirm' && t('admin:regions.commit.title')}
                {step === 'done' && t('admin:regions.commit.successTitle')}
              </h2>
            </div>
            <a
              href="/api/regions/template"
              className="text-xs font-medium text-forest-700 hover:text-forest-800"
              download
            >
              {t('admin:regions.upload.template')}
            </a>
          </div>
          <StepIndicator step={step} t={t} />
        </CardHeader>
        <CardBody className="space-y-4">
          {step === 'upload' && (
            <div className="space-y-3">
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm file:mr-3 file:rounded file:border-0 file:bg-forest-50 file:px-3 file:py-1 file:text-forest-700"
              />
              <div className="flex justify-end">
                <Button
                  onClick={() => file && parseMut.mutate(file)}
                  loading={parseMut.isPending}
                  disabled={!file}
                >
                  <Upload className="mr-1 h-4 w-4" />
                  {t('admin:regions.upload.parse')}
                </Button>
              </div>
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-2 text-xs text-slate-500 sm:grid-cols-3">
                <div>
                  <span className="font-medium">{t('admin:regions.preview.sheet')}: </span>
                  {preview.sheet}
                </div>
                <div>
                  <span className="font-medium">{t('admin:regions.preview.terminationRow')}: </span>
                  {preview.terminationRow}
                </div>
                <div className="truncate">
                  <span className="font-medium">{t('admin:regions.preview.batchId')}: </span>
                  <span className="font-mono">{preview.batchId.slice(0, 8)}…</span>
                </div>
              </div>

              {oosBlocksLabel && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>{t('admin:regions.preview.oosBlocks', { blocks: oosBlocksLabel })}</span>
                </div>
              )}

              <BlockSummary
                title={t('admin:regions.preview.primarii')}
                valid={preview.primarii.validCount}
                invalid={preview.primarii.invalidCount}
                duplicates={preview.primarii.duplicates}
                t={t}
              />
              <BlockSummary
                title={t('admin:regions.preview.entitatiSilvice')}
                valid={preview.entitatiSilvice.validCount}
                invalid={preview.entitatiSilvice.invalidCount}
                duplicates={preview.entitatiSilvice.duplicates}
                t={t}
              />
              <BlockSummary
                title={t('admin:regions.preview.ocoaleSilvice')}
                valid={preview.ocoaleSilvice.validCount}
                invalid={preview.ocoaleSilvice.invalidCount}
                duplicates={preview.ocoaleSilvice.duplicates}
                t={t}
              />
              <BlockSummary
                title={t('admin:regions.preview.treeSpeciesBlock')}
                valid={preview.treeSpecies.validCount}
                invalid={preview.treeSpecies.invalidCount}
                duplicates={preview.treeSpecies.duplicates}
                t={t}
              />
              <BlockSummary
                title={t('admin:regions.preview.workTypesBlock')}
                valid={preview.workTypes.validCount}
                invalid={preview.workTypes.invalidCount}
                duplicates={preview.workTypes.duplicates}
                t={t}
              />

              {preview.errors.length > 0 && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900">
                  <p className="mb-1 font-semibold">
                    {t('admin:regions.preview.errors')} ({preview.errors.length})
                  </p>
                  <ul className="list-inside list-disc space-y-0.5">
                    {preview.errors.slice(0, 10).map((e, i) => (
                      <li key={i}>
                        <span className="font-mono">[{e.block}:{e.row}]</span> {e.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="secondary" onClick={reset}>
                  {t('admin:regions.commit.cancel')}
                </Button>
                <Button onClick={() => setStep('confirm')}>
                  {t('common:action.next', { defaultValue: 'Continuă' })}
                </Button>
              </div>
            </div>
          )}

          {step === 'confirm' && preview && (
            <div className="space-y-4">
              <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700">
                    {t('admin:regions.commit.mode')}
                  </label>
                  <Select
                    value={mode}
                    onChange={(e) => setMode(e.target.value as CommitMode)}
                    className="mt-1 h-8 text-sm"
                  >
                    <option value="skip_invalid">
                      {t('admin:regions.commit.modeSkip')}
                    </option>
                    <option value="all_or_nothing">
                      {t('admin:regions.commit.modeAll')}
                    </option>
                  </Select>
                </div>
                <label
                  className={cn(
                    'flex items-center gap-2 text-sm',
                    hasMutableChanges ? 'text-slate-700' : 'cursor-not-allowed text-slate-400',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={applyNameChanges && hasMutableChanges}
                    disabled={!hasMutableChanges}
                    onChange={(e) => setApplyNameChanges(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-forest-600 focus:ring-forest-500 disabled:opacity-50"
                  />
                  {t('admin:regions.commit.applyNameChanges')}
                </label>
              </div>

              <div className="flex justify-between">
                <Button variant="secondary" onClick={() => setStep('preview')}>
                  {t('common:action.back', { defaultValue: 'Înapoi' })}
                </Button>
                <Button
                  onClick={() =>
                    commitMut.mutate({
                      batchId: preview.batchId,
                      mode,
                      applyNameChanges: applyNameChanges && hasMutableChanges,
                    })
                  }
                  loading={commitMut.isPending}
                >
                  <Check className="mr-1 h-4 w-4" />
                  {t('admin:regions.commit.run')}
                </Button>
              </div>
            </div>
          )}

          {step === 'done' && committed && (
            <div className="space-y-4">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                <p className="font-semibold">{t('admin:regions.commit.successTitle')}</p>
                <p className="text-xs">
                  {t('admin:regions.commit.duration', { ms: committed.durationMs })}
                </p>
              </div>
              <ResultRow
                label={t('admin:regions.preview.primarii')}
                counts={committed.primarii}
                t={t}
              />
              <ResultRow
                label={t('admin:regions.preview.entitatiSilvice')}
                counts={committed.entitatiSilvice}
                t={t}
              />
              <ResultRow
                label={t('admin:regions.preview.ocoaleSilvice')}
                counts={committed.ocoaleSilvice}
                t={t}
              />
              <ResultRow
                label={t('admin:regions.preview.treeSpeciesBlock')}
                counts={committed.treeSpecies}
                t={t}
              />
              <ResultRow
                label={t('admin:regions.preview.workTypesBlock')}
                counts={committed.workTypes}
                t={t}
              />
              <div className="flex justify-end">
                <Button onClick={reset}>{t('admin:regions.upload.parseAgain')}</Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ----------------------------------------------------------------
// Editable reference data (SUPER_ADMIN inline rename)
//
// Each table is rendered in its own collapsible block. Names/codes are
// editable via save-on-blur inputs (mirrors the `cod` field on the admin
// Users page). Inputs are keyed by `id + updatedAt` so a successful save
// — or a reverted failed save (e.g. a 409 unique conflict, which the
// mutation surfaces as a toast and the query invalidation re-fetches) —
// re-mounts the input against the fresh server value rather than leaving
// the stale typed text in place.
// ----------------------------------------------------------------

type RefBlockKey =
  | 'primarii'
  | 'entitati'
  | 'ocoale'
  | 'treeSpecies'
  | 'workTypes'
  | 'projects';

/** Number of Primarie rows rendered at once. Capped so we never paint
 *  908 inputs. Searching narrows the loaded set; the cap then trims the
 *  result so even an empty search shows a manageable slice. */
const PRIMARIE_RENDER_CAP = 100;

function EditableReferenceSection() {
  const { t } = useTranslation(['admin', 'common']);
  const role = useAuthStore((s) => s.user?.role);
  const isSuperAdmin = role === 'SUPER_ADMIN';

  // Only one block open at a time so the page doesn't grow unboundedly.
  const [open, setOpen] = useState<RefBlockKey | null>(null);

  if (!isSuperAdmin) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Pencil className="h-4 w-4 text-slate-500" />
          <div>
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {t('admin:regions.edit.title')}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {t('admin:regions.edit.subtitle')}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardBody className="space-y-2">
        <PrimariiEditBlock
          expanded={open === 'primarii'}
          onToggle={() => setOpen(open === 'primarii' ? null : 'primarii')}
        />
        <EntitatiEditBlock
          expanded={open === 'entitati'}
          onToggle={() => setOpen(open === 'entitati' ? null : 'entitati')}
        />
        <OcoaleEditBlock
          expanded={open === 'ocoale'}
          onToggle={() => setOpen(open === 'ocoale' ? null : 'ocoale')}
        />
        <TreeSpeciesEditBlock
          expanded={open === 'treeSpecies'}
          onToggle={() => setOpen(open === 'treeSpecies' ? null : 'treeSpecies')}
        />
        <WorkTypesEditBlock
          expanded={open === 'workTypes'}
          onToggle={() => setOpen(open === 'workTypes' ? null : 'workTypes')}
        />
        <ProjectsEditBlock
          expanded={open === 'projects'}
          onToggle={() => setOpen(open === 'projects' ? null : 'projects')}
        />
      </CardBody>
    </Card>
  );
}

/** Generic collapsible shell: header (title + count) + optional search +
 *  a scrollable body. Each editable block uses this for consistent chrome. */
function EditBlockShell({
  title,
  count,
  expanded,
  onToggle,
  search,
  onSearch,
  searchPlaceholder,
  loading,
  shownLabel,
  addForm,
  children,
}: {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  search?: string;
  onSearch?: (next: string) => void;
  searchPlaceholder?: string;
  loading?: boolean;
  shownLabel?: string;
  /** Optional inline "add new row" form, pinned above the scrollable
   *  list so it doesn't scroll away with the rows. */
  addForm?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-md border border-slate-200 bg-white transition-shadow dark:border-slate-700 dark:bg-slate-900',
        expanded && 'shadow-md ring-1 ring-forest-200',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {title}
          </span>
          <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            {count}
          </span>
        </span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 flex-shrink-0 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-slate-400" />
        )}
      </button>
      {expanded ? (
        <div className="space-y-2 border-t border-slate-100 px-3 py-2 dark:border-slate-700">
          {onSearch ? (
            <Input
              type="search"
              value={search ?? ''}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={searchPlaceholder}
              autoFocus
            />
          ) : null}
          {addForm}
          {loading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="max-h-96 overflow-auto rounded border border-slate-100 dark:border-slate-700">
                {children}
              </div>
              {shownLabel ? (
                <p className="text-right text-[10px] uppercase tracking-wide text-slate-400">
                  {shownLabel}
                </p>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Save-on-blur text input. Keyed by `id + updatedAt` at the call site so
 * a successful/reverted mutation re-mounts it against the server value.
 * `current` is the server value ('' when null); `onCommit` receives the
 * trimmed next value and is only called when it differs.
 */
function EditableCell({
  current,
  onCommit,
  placeholder,
  maxLength,
  className,
}: {
  current: string;
  onCommit: (next: string) => void;
  placeholder?: string;
  maxLength?: number;
  className?: string;
}) {
  return (
    <input
      type="text"
      defaultValue={current}
      placeholder={placeholder}
      maxLength={maxLength}
      className={cn(
        'w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs focus:border-forest-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100',
        className,
      )}
      onBlur={(e) => {
        const next = e.target.value.trim();
        if (next !== current) onCommit(next);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

function EmptyRows({ search, t }: { search: string; t: (k: string, v?: Record<string, unknown>) => string }) {
  return (
    <p className="px-2 py-4 text-center text-xs italic text-slate-400">
      {search
        ? t('admin:regions.edit.noResults', { defaultValue: 'Niciun rezultat' })
        : '—'}
    </p>
  );
}

/**
 * Shared chrome for an inline "add new row" form rendered above each
 * editable block's table. Renders a small heading + a horizontal row of
 * inputs (supplied by the caller) + an "Adaugă" submit button. The caller
 * owns the field state, validity (`canSubmit`), and the `onSubmit` handler;
 * this shell only standardises the layout, the heading, and the disabled
 * state while the mutation is `pending`.
 */
function AddRowForm({
  onSubmit,
  canSubmit,
  pending,
  children,
}: {
  onSubmit: () => void;
  canSubmit: boolean;
  pending: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation(['admin', 'common']);
  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        if (canSubmit && !pending) onSubmit();
      }}
      className="mb-2 rounded-md border border-dashed border-slate-300 bg-slate-50/60 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/40"
    >
      <p className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        <Plus className="h-3 w-3" />
        {t('admin:regions.add.title')}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        {children}
        <Button
          type="submit"
          size="sm"
          loading={pending}
          disabled={!canSubmit || pending}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          {t('admin:regions.add.submit')}
        </Button>
      </div>
    </form>
  );
}

/** A labelled input slot for the inline add-forms. Compact (xs) inputs to
 *  match the editable-cell density above them. */
function AddFieldInput({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  className,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  maxLength?: number;
  className?: string;
}) {
  return (
    <label className={cn('flex flex-col gap-0.5', className)}>
      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete="off"
        className="h-8 text-xs"
      />
    </label>
  );
}

/** Small hook bundling the success toast that every add-form fires once the
 *  POST resolves. The list refresh is handled by the create hook's query
 *  invalidation; this only surfaces the confirmation message. */
function useAddSuccessToast() {
  const { t } = useTranslation(['admin', 'common']);
  const pushToast = useUiStore((s) => s.pushToast);
  return (label: string) =>
    pushToast({
      variant: 'success',
      message: t('admin:regions.add.success', { label }),
    });
}

function AddPrimarieForm() {
  const { t } = useTranslation(['admin', 'common']);
  const create = useCreatePrimarie();
  const onSuccess = useAddSuccessToast();
  const [cod, setCod] = useState('');
  const [name, setName] = useState('');
  const [raion, setRaion] = useState('');

  const canSubmit = !!cod.trim() && !!name.trim() && !!raion.trim();

  const submit = () => {
    create.mutate(
      { cod: cod.trim(), name: name.trim(), raion: raion.trim() },
      {
        // On success: clear the form so the next entry starts blank. On
        // error (e.g. 409 duplicate cod) the create hook toasts the server
        // message and we deliberately KEEP the field values so the user can
        // fix and retry.
        onSuccess: () => {
          onSuccess(cod.trim());
          setCod('');
          setName('');
          setRaion('');
        },
      },
    );
  };

  return (
    <AddRowForm onSubmit={submit} canSubmit={canSubmit} pending={create.isPending}>
      <AddFieldInput
        label={t('admin:regions.add.cod')}
        value={cod}
        onChange={setCod}
        placeholder={t('admin:regions.add.codPrimariePlaceholder')}
        maxLength={32}
        className="w-24"
      />
      <AddFieldInput
        label={t('admin:regions.add.name')}
        value={name}
        onChange={setName}
        placeholder={t('admin:regions.add.namePrimariePlaceholder')}
        maxLength={200}
        className="min-w-[10rem] flex-1"
      />
      <AddFieldInput
        label={t('admin:regions.add.raion')}
        value={raion}
        onChange={setRaion}
        placeholder={t('admin:regions.add.raionPlaceholder')}
        maxLength={100}
        className="w-40"
      />
    </AddRowForm>
  );
}

function AddOcolSilvicForm() {
  const { t } = useTranslation(['admin', 'common']);
  const create = useCreateOcolSilvic();
  const onSuccess = useAddSuccessToast();
  const entitatiQ = useEntitatiSilvice();
  const entitati = entitatiQ.data?.items ?? [];
  const [cod, setCod] = useState('');
  const [entitateSilvicaId, setEntitateSilvicaId] = useState('');

  const canSubmit = !!cod.trim() && !!entitateSilvicaId;

  const submit = () => {
    create.mutate(
      { cod: cod.trim(), entitateSilvicaId },
      {
        onSuccess: () => {
          onSuccess(cod.trim());
          setCod('');
          setEntitateSilvicaId('');
        },
      },
    );
  };

  return (
    <AddRowForm onSubmit={submit} canSubmit={canSubmit} pending={create.isPending}>
      <AddFieldInput
        label={t('admin:regions.add.cod')}
        value={cod}
        onChange={setCod}
        placeholder={t('admin:regions.add.codOcolPlaceholder')}
        maxLength={64}
        className="w-32"
      />
      <label className="flex min-w-[12rem] flex-1 flex-col gap-0.5">
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {t('admin:regions.add.entitateSilvica')}
        </span>
        <Select
          value={entitateSilvicaId}
          onChange={(e) => setEntitateSilvicaId(e.target.value)}
          className="h-8 text-xs"
        >
          <option value="">
            {t('admin:regions.add.entitateSilvicaPlaceholder')}
          </option>
          {entitati.map((ent: EntitateSilvicaDto) => (
            <option key={ent.id} value={ent.id}>
              {ent.cod} — {ent.name}
            </option>
          ))}
        </Select>
      </label>
    </AddRowForm>
  );
}

function AddEntitateSilvicaInlineForm() {
  const { t } = useTranslation(['admin', 'common']);
  const create = useCreateEntitateSilvica();
  const onSuccess = useAddSuccessToast();
  const [cod, setCod] = useState('');
  const [name, setName] = useState('');

  const canSubmit = !!cod.trim() && !!name.trim();

  const submit = () => {
    create.mutate(
      { cod: cod.trim(), name: name.trim() },
      {
        onSuccess: () => {
          onSuccess(cod.trim());
          setCod('');
          setName('');
        },
      },
    );
  };

  return (
    <AddRowForm onSubmit={submit} canSubmit={canSubmit} pending={create.isPending}>
      <AddFieldInput
        label={t('admin:regions.add.cod')}
        value={cod}
        onChange={setCod}
        placeholder={t('admin:regions.addEntitate.codLabel', { defaultValue: 'Cod' })}
        maxLength={32}
        className="w-28"
      />
      <AddFieldInput
        label={t('admin:regions.add.name')}
        value={name}
        onChange={setName}
        placeholder={t('admin:regions.addEntitate.nameLabel', { defaultValue: 'Nume canonic' })}
        maxLength={200}
        className="min-w-[10rem] flex-1"
      />
    </AddRowForm>
  );
}

function AddTreeSpeciesForm() {
  const { t } = useTranslation(['admin', 'common']);
  const create = useCreateTreeSpecies();
  const onSuccess = useAddSuccessToast();
  const [cod, setCod] = useState('');
  const [name, setName] = useState('');

  const canSubmit = !!cod.trim();

  const submit = () => {
    create.mutate(
      { cod: cod.trim(), name: name.trim() || null },
      {
        onSuccess: () => {
          onSuccess(cod.trim());
          setCod('');
          setName('');
        },
      },
    );
  };

  return (
    <AddRowForm onSubmit={submit} canSubmit={canSubmit} pending={create.isPending}>
      <AddFieldInput
        label={t('admin:regions.add.cod')}
        value={cod}
        onChange={setCod}
        placeholder={t('admin:regions.add.codSpeciePlaceholder')}
        maxLength={16}
        className="w-28"
      />
      <AddFieldInput
        label={t('admin:regions.add.nameOptional')}
        value={name}
        onChange={setName}
        placeholder={t('admin:regions.add.nameSpeciePlaceholder')}
        maxLength={200}
        className="min-w-[10rem] flex-1"
      />
    </AddRowForm>
  );
}

function AddWorkTypeForm() {
  const { t } = useTranslation(['admin', 'common']);
  const create = useCreateWorkType();
  const onSuccess = useAddSuccessToast();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const canSubmit = !!name.trim();

  const submit = () => {
    create.mutate(
      { name: name.trim(), code: code.trim() || null },
      {
        onSuccess: () => {
          onSuccess(name.trim());
          setName('');
          setCode('');
        },
      },
    );
  };

  return (
    <AddRowForm onSubmit={submit} canSubmit={canSubmit} pending={create.isPending}>
      <AddFieldInput
        label={t('admin:regions.add.codeOptional')}
        value={code}
        onChange={setCode}
        placeholder={t('admin:regions.add.codeWorkTypePlaceholder')}
        maxLength={32}
        className="w-28"
      />
      <AddFieldInput
        label={t('admin:regions.add.name')}
        value={name}
        onChange={setName}
        placeholder={t('admin:regions.add.nameWorkTypePlaceholder')}
        maxLength={400}
        className="min-w-[12rem] flex-1"
      />
    </AddRowForm>
  );
}

function AddProjectForm() {
  const { t } = useTranslation(['admin', 'common']);
  const create = useCreateProjectType();
  const onSuccess = useAddSuccessToast();
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const canSubmit = !!name.trim();

  const submit = () => {
    const parsed = sortOrder.trim() ? Number(sortOrder.trim()) : null;
    create.mutate(
      {
        name: name.trim(),
        ...(parsed !== null && Number.isFinite(parsed)
          ? { sortOrder: parsed }
          : {}),
      },
      {
        onSuccess: () => {
          onSuccess(name.trim());
          setName('');
          setSortOrder('');
        },
      },
    );
  };

  return (
    <AddRowForm onSubmit={submit} canSubmit={canSubmit} pending={create.isPending}>
      <AddFieldInput
        label={t('admin:regions.add.name')}
        value={name}
        onChange={setName}
        placeholder={t('admin:regions.add.nameProjectPlaceholder')}
        maxLength={100}
        className="min-w-[12rem] flex-1"
      />
      <AddFieldInput
        label={t('admin:regions.add.sortOrderOptional')}
        value={sortOrder}
        onChange={(next) => setSortOrder(next.replace(/[^0-9-]/g, ''))}
        placeholder={t('admin:regions.add.sortOrderPlaceholder')}
        maxLength={6}
        className="w-24"
      />
    </AddRowForm>
  );
}

function PrimariiEditBlock({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const q = useAllPrimarii();
  const update = useUpdatePrimarie();
  const [search, setSearch] = useState('');

  const all = q.data?.items ?? [];
  const term = search.trim().toLowerCase();
  const matched = useMemo(() => {
    if (!term) return all;
    return all.filter(
      (p) =>
        p.cod.toLowerCase().includes(term) ||
        p.name.toLowerCase().includes(term) ||
        (p.raion ?? '').toLowerCase().includes(term),
    );
  }, [all, term]);
  const rows = matched.slice(0, PRIMARIE_RENDER_CAP);

  return (
    <EditBlockShell
      title={t('admin:regions.edit.primarii')}
      count={q.data?.pagination.total ?? all.length}
      expanded={expanded}
      onToggle={onToggle}
      search={search}
      onSearch={setSearch}
      searchPlaceholder={t('admin:regions.edit.searchPlaceholder')}
      loading={q.isLoading}
      shownLabel={t('admin:regions.edit.shown', {
        count: rows.length,
        total: matched.length,
        defaultValue: '{{count}} / {{total}}',
      })}
      addForm={<AddPrimarieForm />}
    >
      {rows.length === 0 ? (
        <EmptyRows search={search} t={t} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th className="w-24">{t('admin:regions.edit.col.cod')}</Th>
              <Th>{t('admin:regions.edit.col.name')}</Th>
              <Th className="w-48">{t('admin:regions.edit.col.raion')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p: PrimarieDto) => (
              <tr key={`${p.id}:${p.updatedAt}`}>
                <Td className="font-mono text-xs text-slate-500">{p.cod}</Td>
                <Td>
                  <EditableCell
                    current={p.name ?? ''}
                    maxLength={200}
                    onCommit={(next) => {
                      if (!next) return; // name is required (min 1)
                      update.mutate({ id: p.id, data: { name: next } });
                    }}
                  />
                </Td>
                <Td>
                  <EditableCell
                    current={p.raion ?? ''}
                    maxLength={100}
                    onCommit={(next) => {
                      if (!next) return; // raion is required (min 1)
                      update.mutate({ id: p.id, data: { raion: next } });
                    }}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </EditBlockShell>
  );
}

function EntitatiEditBlock({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const q = useEntitatiSilvice();
  const update = useUpdateEntitateSilvica();
  const rows = q.data?.items ?? [];

  return (
    <EditBlockShell
      title={t('admin:regions.edit.entitatiSilvice')}
      count={q.data?.total ?? rows.length}
      expanded={expanded}
      onToggle={onToggle}
      loading={q.isLoading}
      addForm={<AddEntitateSilvicaInlineForm />}
    >
      {rows.length === 0 ? (
        <EmptyRows search="" t={t} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th className="w-24">{t('admin:regions.edit.col.cod')}</Th>
              <Th>{t('admin:regions.edit.col.name')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e: EntitateSilvicaDto) => (
              <tr key={`${e.id}:${e.updatedAt}`}>
                <Td className="font-mono text-xs text-slate-500">{e.cod}</Td>
                <Td>
                  <EditableCell
                    current={e.name ?? ''}
                    maxLength={200}
                    onCommit={(next) => {
                      if (!next) return; // name is required (min 1)
                      update.mutate({ id: e.id, data: { name: next } });
                    }}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </EditBlockShell>
  );
}

function OcoaleEditBlock({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const q = useOcoaleSilvice();
  const update = useUpdateOcolSilvic();
  const [search, setSearch] = useState('');

  const all = q.data?.items ?? [];
  const term = search.trim().toLowerCase();
  const rows = useMemo(() => {
    if (!term) return all;
    return all.filter(
      (o) =>
        o.cod.toLowerCase().includes(term) ||
        o.entitateSilvicaCod.toLowerCase().includes(term),
    );
  }, [all, term]);

  return (
    <EditBlockShell
      title={t('admin:regions.edit.ocoaleSilvice')}
      count={q.data?.pagination.total ?? all.length}
      expanded={expanded}
      onToggle={onToggle}
      search={search}
      onSearch={setSearch}
      searchPlaceholder={t('admin:regions.edit.searchPlaceholder')}
      loading={q.isLoading}
      shownLabel={t('admin:regions.edit.shown', {
        count: rows.length,
        total: all.length,
        defaultValue: '{{count}} / {{total}}',
      })}
      addForm={<AddOcolSilvicForm />}
    >
      {rows.length === 0 ? (
        <EmptyRows search={search} t={t} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('admin:regions.edit.col.cod')}</Th>
              <Th className="w-40">
                {t('admin:regions.edit.col.entitateSilvica')}
              </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o: OcolSilvicDto) => (
              <tr key={`${o.id}:${o.updatedAt}`}>
                <Td>
                  <EditableCell
                    current={o.cod ?? ''}
                    maxLength={64}
                    className="font-mono"
                    onCommit={(next) => {
                      if (!next) return; // cod is required (min 1)
                      update.mutate({ id: o.id, data: { cod: next } });
                    }}
                  />
                </Td>
                <Td className="font-mono text-xs text-slate-500">
                  {o.entitateSilvicaCod}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </EditBlockShell>
  );
}

function TreeSpeciesEditBlock({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const q = useTreeSpecies();
  const update = useUpdateTreeSpecies();
  const [search, setSearch] = useState('');

  const all = q.data?.items ?? [];
  const term = search.trim().toLowerCase();
  const rows = useMemo(() => {
    if (!term) return all;
    return all.filter(
      (s) =>
        s.cod.toLowerCase().includes(term) ||
        (s.name ?? '').toLowerCase().includes(term),
    );
  }, [all, term]);

  return (
    <EditBlockShell
      title={t('admin:regions.edit.treeSpecies')}
      count={all.length}
      expanded={expanded}
      onToggle={onToggle}
      search={search}
      onSearch={setSearch}
      searchPlaceholder={t('admin:regions.edit.searchPlaceholder')}
      loading={q.isLoading}
      shownLabel={t('admin:regions.edit.shown', {
        count: rows.length,
        total: all.length,
        defaultValue: '{{count}} / {{total}}',
      })}
      addForm={<AddTreeSpeciesForm />}
    >
      {rows.length === 0 ? (
        <EmptyRows search={search} t={t} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th className="w-24">{t('admin:regions.edit.col.cod')}</Th>
              <Th>{t('admin:regions.edit.col.name')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s: TreeSpeciesDto) => (
              <tr key={`${s.id}:${s.updatedAt}`}>
                <Td className="font-mono text-xs text-slate-500">{s.cod}</Td>
                <Td>
                  <EditableCell
                    current={s.name ?? ''}
                    maxLength={200}
                    placeholder="—"
                    onCommit={(next) => {
                      // name is nullable — empty clears it.
                      update.mutate({ id: s.id, data: { name: next || null } });
                    }}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </EditBlockShell>
  );
}

function WorkTypesEditBlock({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const q = useWorkTypes();
  const update = useUpdateWorkType();
  const [search, setSearch] = useState('');

  const all = q.data?.items ?? [];
  const term = search.trim().toLowerCase();
  const rows = useMemo(() => {
    if (!term) return all;
    return all.filter(
      (w) =>
        w.name.toLowerCase().includes(term) ||
        (w.code ?? '').toLowerCase().includes(term),
    );
  }, [all, term]);

  return (
    <EditBlockShell
      title={t('admin:regions.edit.workTypes')}
      count={all.length}
      expanded={expanded}
      onToggle={onToggle}
      search={search}
      onSearch={setSearch}
      searchPlaceholder={t('admin:regions.edit.searchPlaceholder')}
      loading={q.isLoading}
      shownLabel={t('admin:regions.edit.shown', {
        count: rows.length,
        total: all.length,
        defaultValue: '{{count}} / {{total}}',
      })}
      addForm={<AddWorkTypeForm />}
    >
      {rows.length === 0 ? (
        <EmptyRows search={search} t={t} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th className="w-28">{t('admin:regions.edit.col.code')}</Th>
              <Th>{t('admin:regions.edit.col.name')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((w: WorkTypeDto) => (
              <tr key={`${w.id}:${w.updatedAt}`}>
                <Td className="font-mono text-xs text-slate-500">
                  {w.code ?? '—'}
                </Td>
                <Td>
                  <EditableCell
                    current={w.name ?? ''}
                    maxLength={400}
                    onCommit={(next) => {
                      // name is the unique key (required, min 1). On a 409
                      // conflict the mutation toasts + invalidates; the
                      // re-fetch re-keys the input back to the server value.
                      if (!next) return;
                      update.mutate({ id: w.id, data: { name: next } });
                    }}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </EditBlockShell>
  );
}

function ProjectsEditBlock({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const q = useProjectTypes();
  const update = useUpdateProjectType();
  const [search, setSearch] = useState('');

  const all = q.data?.items ?? [];
  const term = search.trim().toLowerCase();
  const rows = useMemo(() => {
    if (!term) return all;
    return all.filter((p) => p.name.toLowerCase().includes(term));
  }, [all, term]);

  return (
    <EditBlockShell
      title={t('admin:regions.edit.projects')}
      count={all.length}
      expanded={expanded}
      onToggle={onToggle}
      search={search}
      onSearch={setSearch}
      searchPlaceholder={t('admin:regions.edit.searchPlaceholder')}
      loading={q.isLoading}
      shownLabel={t('admin:regions.edit.shown', {
        count: rows.length,
        total: all.length,
        defaultValue: '{{count}} / {{total}}',
      })}
      addForm={<AddProjectForm />}
    >
      {rows.length === 0 ? (
        <EmptyRows search={search} t={t} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('admin:regions.edit.col.name')}</Th>
              <Th className="w-24">{t('admin:regions.edit.col.sortOrder')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p: ProjectTypeDto) => (
              <tr key={`${p.id}:${p.updatedAt}`}>
                <Td>
                  <EditableCell
                    current={p.name ?? ''}
                    maxLength={100}
                    onCommit={(next) => {
                      // name is the unique key (required, min 1). On a 409
                      // conflict the mutation toasts + invalidates; the
                      // re-fetch re-keys the input back to the server value.
                      if (!next) return;
                      update.mutate({ id: p.id, data: { name: next } });
                    }}
                  />
                </Td>
                <Td className="font-mono text-xs text-slate-500">
                  {p.sortOrder ?? '—'}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </EditBlockShell>
  );
}

function StepIndicator({
  step,
  t,
}: {
  step: Step;
  t: (k: string, v?: Record<string, unknown>) => string;
}) {
  const order: Step[] = ['upload', 'preview', 'confirm', 'done'];
  const current = order.indexOf(step);
  const labels: Record<Step, string> = {
    upload: t('admin:regions.steps.upload'),
    preview: t('admin:regions.steps.preview'),
    confirm: t('admin:regions.steps.confirm'),
    done: t('admin:regions.steps.done'),
  };
  return (
    <div className="flex items-center gap-1.5">
      {order.map((s, i) => (
        <div key={s} className="flex flex-1 items-center gap-1.5">
          <div
            className={cn(
              'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
              i < current && 'bg-forest-100 text-forest-700',
              i === current && 'bg-forest-600 text-white',
              i > current && 'bg-slate-100 text-slate-400',
            )}
          >
            {i < current ? <Check className="h-3 w-3" /> : i + 1}
          </div>
          <span
            className={cn(
              'truncate text-[10px] font-medium uppercase tracking-wide',
              i === current ? 'text-slate-700' : 'text-slate-400',
            )}
          >
            {labels[s]}
          </span>
          {i < order.length - 1 ? (
            <span
              className={cn(
                'mx-1 h-px flex-1',
                i < current ? 'bg-forest-300' : 'bg-slate-200',
              )}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}

type StateRow = { key: string; text: string; extra?: string };

function StateBlock({
  label,
  rows,
  expanded,
  onToggle,
  search,
  onSearch,
  t,
}: {
  label: string;
  rows: StateRow[];
  expanded: boolean;
  onToggle: () => void;
  search: string;
  onSearch: (next: string) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  // Filter for the expanded view; collapsed view always shows the
  // first 5 unfiltered so the at-a-glance preview stays predictable.
  const filtered = useMemo(() => {
    if (!expanded) return rows.slice(0, 5);
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.text.toLowerCase().includes(q) ||
        (r.extra ?? '').toLowerCase().includes(q),
    );
  }, [rows, search, expanded]);

  return (
    <div
      className={cn(
        'flex flex-col rounded-md border border-slate-200 bg-white p-3 transition-shadow',
        expanded && 'shadow-md ring-1 ring-forest-200',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 text-left"
        aria-expanded={expanded}
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </p>
        {expanded ? (
          <ChevronUp className="h-4 w-4 flex-shrink-0 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-slate-400" />
        )}
      </button>
      {expanded ? (
        <div className="mt-2 space-y-2">
          <Input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={t('admin:regions.state.searchPlaceholder', {
              defaultValue: 'Caută...',
            })}
            autoFocus
          />
          <div className="max-h-80 overflow-auto rounded border border-slate-100 bg-slate-50/40">
            {filtered.length === 0 ? (
              <p className="px-2 py-3 text-center text-xs italic text-slate-400">
                {search
                  ? t('admin:regions.state.noResults', {
                      defaultValue: 'Niciun rezultat',
                    })
                  : '—'}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 text-xs">
                {filtered.map((r) => (
                  <li
                    key={r.key}
                    className="flex items-baseline justify-between gap-2 px-2 py-1"
                  >
                    <span className="truncate text-slate-700">{r.text}</span>
                    {r.extra ? (
                      <span className="flex-shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                        {r.extra}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="text-right text-[10px] uppercase tracking-wide text-slate-400">
            {t('admin:regions.state.shown', {
              count: filtered.length,
              total: rows.length,
              defaultValue: '{{count}} / {{total}}',
            })}
          </p>
        </div>
      ) : (
        <ul className="mt-2 space-y-0.5 text-xs text-slate-600">
          {filtered.length === 0 ? (
            <li className="italic text-slate-400">—</li>
          ) : (
            filtered.map((r) => (
              <li key={r.key} className="flex items-baseline justify-between gap-2">
                <span className="truncate">{r.text}</span>
                {r.extra ? (
                  <span className="flex-shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                    {r.extra}
                  </span>
                ) : null}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function BlockSummary({
  title,
  valid,
  invalid,
  duplicates,
  t,
}: {
  title: string;
  valid: number;
  invalid: number;
  duplicates: RegionsDuplicate[];
  t: (k: string, v?: Record<string, unknown>) => string;
}) {
  const reasonKey = (r: RegionsDuplicate['reason']) =>
    ({
      'duplicate-in-file': 'admin:regions.duplicates.reasonDuplicateInFile',
      'already-imported': 'admin:regions.duplicates.reasonAlreadyImported',
      'name-changed': 'admin:regions.duplicates.reasonNameChanged',
      'membership-changed': 'admin:regions.duplicates.reasonMembershipChanged',
    })[r];

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        <div className="flex items-center gap-3 text-xs">
          <span className="font-medium text-emerald-700">
            {t('admin:regions.preview.valid')}: {valid}
          </span>
          <span
            className={cn(
              'font-medium',
              invalid > 0 ? 'text-red-700' : 'text-slate-400',
            )}
          >
            {t('admin:regions.preview.invalid')}: {invalid}
          </span>
          <span
            className={cn(
              'font-medium',
              duplicates.length > 0 ? 'text-amber-700' : 'text-slate-400',
            )}
          >
            {t('admin:regions.preview.duplicates')}: {duplicates.length}
          </span>
        </div>
      </div>
      {duplicates.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-slate-600">
          {duplicates.slice(0, 5).map((d, i) => (
            <li key={i}>
              <span className="font-mono text-slate-500">{d.cod}</span>{' '}
              <span className="italic text-slate-400">
                {t('admin:regions.duplicates.rows', { rows: d.occursAt.join(', ') })}
              </span>{' '}
              · {t(reasonKey(d.reason))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ResultRow({
  label,
  counts,
  t,
}: {
  label: string;
  counts: { inserted: number; updated: number; skipped: number };
  t: (k: string) => string;
}) {
  return (
    <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      <div className="flex items-center gap-4 text-xs">
        <span className="text-emerald-700">
          {t('admin:regions.result.inserted')}: <strong>{counts.inserted}</strong>
        </span>
        <span className="text-sky-700">
          {t('admin:regions.result.updated')}: <strong>{counts.updated}</strong>
        </span>
        <span className="text-slate-500">
          {t('admin:regions.result.skipped')}: <strong>{counts.skipped}</strong>
        </span>
      </div>
    </div>
  );
}
